import { COLORS } from "../../shared/constants";
import { useState, useEffect } from "react";
import * as api from "../../api";

export default // ─── Devices Tab (Settings) ────────────────────────────────
function DevicesTab() {
  const [connected, setConnected] = useState(false);
  const [simulated, setSimulated] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    refreshStatus();
  }, []);

  const refreshStatus = async () => {
    try {
      const s = await api.deviceStatus();
      setConnected(s.connected || false);
      setSimulated(s.simulated || false);
      setStatus(s);
    } catch (err) {
      setConnected(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true); setTestResult(null); setError(null);
    try {
      const connectResult = await api.deviceConnect();
      if (connectResult.success) {
        const statusResult = await api.deviceStatus();
        await api.deviceDisconnect();
        setTestResult({
          success: true,
          model: statusResult.model,
          serial_no: statusResult.serial_no,
          port: connectResult.port,
          simulated: connectResult.simulated,
        });
      } else {
        setTestResult({ success: false, error: connectResult.message });
      }
    } catch (err) {
      setTestResult({ success: false, error: err.error || "Connection failed" });
    }
    setLoading(false);
    refreshStatus();
  };

  const handleConnect = async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.deviceConnect();
      setConnected(result.success);
      setSimulated(result.simulated || false);
      if (!result.success) setError(result.message);
    } catch (err) { setError(err.error || "Connection failed"); }
    setLoading(false);
    refreshStatus();
  };

  const handleDisconnect = async () => {
    try {
      await api.deviceDisconnect();
      setConnected(false); setStatus(null);
    } catch (err) { setError(err.error || "Disconnect failed"); }
    refreshStatus();
  };

  return (
    <div>
      <h3 style={{ fontSize: "16px", fontWeight: "600", color: COLORS.text, marginBottom: "20px" }}>Device Configuration</h3>

      {/* Connection Status */}
      <div style={{ background: connected ? "#E8F8F0" : COLORS.warningLight, borderRadius: "10px", padding: "16px", marginBottom: "20px", border: `1px solid ${connected ? "#C8E6D9" : COLORS.warningBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: connected ? "#2ECC71" : "#E74C3C" }} />
          <span style={{ fontSize: "15px", fontWeight: "600", color: connected ? COLORS.accent : COLORS.text }}>
            {connected ? (simulated ? "Connected (Simulated Mode)" : "Connected to MagPro R30") : "Not Connected"}
          </span>
        </div>
        {connected && status && (
          <div style={{ fontSize: "13px", color: COLORS.textSecondary, marginLeft: "22px" }}>
            {status.model && <span>Model: {status.model} · </span>}
            {status.serial_no && <span>Serial: {status.serial_no} · </span>}
            {status.port && <span>Port: {status.port}</span>}
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: "12px 16px", background: COLORS.dangerLight, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: "10px", marginBottom: "16px", fontSize: "13px", color: COLORS.danger }}>
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
        {!connected ? (
          <button onClick={handleConnect} disabled={loading}
            style={{ height: "40px", padding: "0 20px", borderRadius: "8px", border: "none", background: COLORS.primary, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: loading ? "wait" : "pointer" }}>
            {loading ? "Connecting…" : "Connect"}
          </button>
        ) : (
          <button onClick={handleDisconnect}
            style={{ height: "40px", padding: "0 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
            Disconnect
          </button>
        )}
        <button onClick={handleTestConnection} disabled={loading}
          style={{ height: "40px", padding: "0 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: "13px", fontWeight: "600", cursor: loading ? "wait" : "pointer" }}>
          {loading ? "Testing…" : "Test Connection"}
        </button>
        <button onClick={refreshStatus}
          style={{ height: "40px", padding: "0 20px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "13px", cursor: "pointer" }}>
          Refresh
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div style={{ padding: "14px 16px", background: testResult.success ? "#E8F8F0" : COLORS.dangerLight, border: `1px solid ${testResult.success ? "#C8E6D9" : COLORS.dangerBorder}`, borderRadius: "10px", marginBottom: "20px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: testResult.success ? COLORS.accent : COLORS.danger, marginBottom: "4px" }}>
            {testResult.success ? "✅ Connection test passed" : "❌ Connection test failed"}
          </div>
          {testResult.success ? (
            <div style={{ fontSize: "12px", color: COLORS.textSecondary }}>
              Model: {testResult.model} · Serial: {testResult.serial_no} · Port: {testResult.port}
              {testResult.simulated && <span style={{ color: "#3498DB" }}> (simulated)</span>}
            </div>
          ) : (
            <div style={{ fontSize: "12px", color: COLORS.danger }}>{testResult.error}</div>
          )}
        </div>
      )}

      {/* Configuration Info */}
      <div style={{ background: COLORS.surface, borderRadius: "10px", border: `1px solid ${COLORS.borderLight}`, padding: "20px" }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "16px" }}>Configuration</div>

        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "12px", fontSize: "13px" }}>
          <span style={{ color: COLORS.textSecondary }}>Serial Port</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>
            {status?.port || "Auto-detect (set DEVICE_SERIAL_PORT in .env)"}
          </span>

          <span style={{ color: COLORS.textSecondary }}>Baud Rate</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>38400 (8N1)</span>

          <span style={{ color: COLORS.textSecondary }}>Mode</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>
            {simulated ? "Simulated (DEVICE_ENABLED=false)" : "Hardware (DEVICE_ENABLED=true)"}
          </span>

          <span style={{ color: COLORS.textSecondary }}>Temperature Unit</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>
            °C only <span style={{ fontSize: "11px", color: COLORS.textSecondary }}>(°F causes device malfunction — do not change)</span>
          </span>

          <span style={{ color: COLORS.textSecondary }}>Telemetry Polling</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>Every 1.5 seconds during treatment</span>

          <span style={{ color: COLORS.textSecondary }}>Fallback Mode</span>
          <span style={{ color: COLORS.text, fontWeight: "500" }}>Manual entry if device unavailable</span>
        </div>

        <div style={{ marginTop: "20px", padding: "12px 16px", background: "#F0F4FF", borderRadius: "8px", fontSize: "12px", color: COLORS.textSecondary, lineHeight: "1.6" }}>
          <strong style={{ color: COLORS.text }}>Setup:</strong> Set <code style={{ background: "#E8ECF4", padding: "1px 4px", borderRadius: "3px" }}>DEVICE_ENABLED=true</code> and <code style={{ background: "#E8ECF4", padding: "1px 4px", borderRadius: "3px" }}>DEVICE_SERIAL_PORT=/dev/cu.usbserial-XXXX</code> in your <code style={{ background: "#E8ECF4", padding: "1px 4px", borderRadius: "3px" }}>.env</code> file, then restart the server. Use <code style={{ background: "#E8ECF4", padding: "1px 4px", borderRadius: "3px" }}>ls /dev/cu.usb*</code> on Mac to find your serial port after plugging in the FTDI adapter.
        </div>
      </div>
    </div>
  );
}
